import Button from "@mui/material/Button";
import React, { useEffect, useState, useContext } from "react";
import {
  GridColDef,
  GridRowSelectionModel,
  GridPaginationModel,
  GridToolbar,
  GridRowParams,
  GridRowClassNameParams,
  useGridApiRef,
  GridDensity,
  GridInitialState,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridRowId,
} from "@mui/x-data-grid";
import {
  IconButton,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import LoadingBackdrop from "../components/loading-backdrop";
import { UserContext } from "../components/app-routes";
import Page from "../components/page";
import { StripedDataGrid } from "../components/striped-data-grid";
import { DateTime } from "luxon";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { ActiveFilterControls } from "../components/active-filter-controls";
import { getSearchParamNumber } from "../utils/history";
import { useHistory } from "react-router-dom";

type ProductionUnitType =
  | "bag"
  | "tray"
  | "block"
  | "log"
  | "bottle"
  | "column"
  | "other";

type WeightUom = "lb" | "kg";
type DimUom = "in" | "cm";

interface ProductionUnit {
  id: number;
  name: string;
  type: ProductionUnitType;

  // Optional specs (use what you have)
  wetWeight: number | null;
  wetWeightUom: WeightUom | null;

  drySubstrateWeight: number | null;
  drySubstrateWeightUom: WeightUom | null;

  length: number | null;
  width: number | null;
  depth: number | null;
  dimUom: DimUom | null;

  notes: string | null;
  archivedAt: string | null;
}

const productionUnitsStorageKey = "mushroomProductionUnits";
const productionUnitsGridStateStorageKey = "mushroomProductionUnitsGridState";
const productionUnitsGridDensityStorageKey =
  "mushroomProductionUnitsGridDensity";

const typeLabel = (t: ProductionUnitType) => {
  switch (t) {
    case "bag":
      return "Bag";
    case "tray":
      return "Tray";
    case "block":
      return "Block";
    case "log":
      return "Log";
    case "bottle":
      return "Bottle";
    case "column":
      return "Column";
    default:
      return "Other";
  }
};

const fmtWeight = (v: number | null, u: WeightUom | null) =>
  v === null || v === undefined || !u ? "" : `${v} ${u}`;

const fmtDims = (
  l: number | null,
  w: number | null,
  d: number | null,
  u: DimUom | null,
) => {
  if (!u) return "";
  const parts = [l, w, d].filter((x) => typeof x === "number") as number[];
  if (!parts.length) return "";
  return parts.join("×") + ` ${u}`;
};

const ProductionUnitsDataGrid = React.memo(
  (props: {
    units: ProductionUnit[] | null;
    onSelectionChange: (params: GridRowSelectionModel) => void;
    initialPage?: number;
    onPaginationModelChange: (m: GridPaginationModel) => void;
    onClickRow: (id: GridRowId) => void;
  }) => {
    const context = useContext(UserContext);
    const grower = context.grower;

    const csvFileName = React.useMemo(() => {
      const zone =
        (grower as any)?.TimeZone ||
        (grower as any)?.timeZone ||
        DateTime.local().zoneName;

      const now = DateTime.local().setZone(zone);
      return `SeedLeaf Mushroom Production Units - ${now.toFormat(
        "MMM dd, yyyy",
      )}`;
    }, [grower]);

    const apiRef = useGridApiRef();
    const [initialState, setInitialState] = React.useState<GridInitialState>();

    const [paginationModel, setPaginationModel] =
      React.useState<GridPaginationModel>(() => {
        const raw = localStorage.getItem(productionUnitsGridStateStorageKey);
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            const saved = parsed?.pagination?.paginationModel;
            if (saved?.pageSize) {
              return {
                page: props.initialPage ?? saved.page ?? 0,
                pageSize: saved.pageSize,
              };
            }
          } catch {}
        }
        return { page: props.initialPage ?? 0, pageSize: 15 };
      });

    const saveSnapshot = React.useCallback(() => {
      if (apiRef?.current?.exportState) {
        const currentState = apiRef.current.exportState();

        if (!(currentState as any)?.pagination?.paginationModel?.pageSize) {
          (currentState as any).pagination = {
            paginationModel: { page: 0, pageSize: 25 },
          };
        }

        localStorage.setItem(
          productionUnitsGridStateStorageKey,
          JSON.stringify(currentState),
        );
      }
    }, [apiRef]);

    React.useLayoutEffect(() => {
      const raw = localStorage?.getItem(productionUnitsGridStateStorageKey);
      let parsed: GridInitialState | undefined;

      if (raw) {
        try {
          parsed = JSON.parse(raw);
        } catch {
          localStorage.removeItem(productionUnitsGridStateStorageKey);
        }
      }

      setInitialState({
        ...parsed,
        columns: {
          columnVisibilityModel: {
            notes: false,
            ...(parsed?.columns?.columnVisibilityModel ?? {}),
          },
          orderedFields: [],
        },
        filter: undefined,
        sorting: parsed?.sorting ?? {
          sortModel: [{ field: "name", sort: "asc" }],
        },
        pagination: {
          paginationModel: {
            page:
              (parsed as any)?.pagination?.paginationModel?.page ??
              props.initialPage ??
              0,
            pageSize:
              (parsed as any)?.pagination?.paginationModel?.pageSize ?? 15,
          },
        },
      });

      window.addEventListener("beforeunload", saveSnapshot);
      return () => {
        window.removeEventListener("beforeunload", saveSnapshot);
        saveSnapshot();
      };
    }, [saveSnapshot, props.initialPage]);

    const [density, setDensity] = React.useState<GridDensity>(() => {
      const d = localStorage.getItem(
        productionUnitsGridDensityStorageKey,
      ) as GridDensity | null;
      return d === "compact" || d === "comfortable" || d === "standard"
        ? d
        : "standard";
    });

    const columns: GridColDef[] = [
      { ...GRID_CHECKBOX_SELECTION_COL_DEF, hideable: false, headerName: "" },
      {
        field: "archived",
        headerName: "",
        width: 35,
        minWidth: 35,
        sortable: false,
        filterable: false,
        align: "center",
        disableColumnMenu: true,
        disableExport: true,
        renderCell: (params) =>
          params.row.archivedAt ? (
            <Inventory2OutlinedIcon fontSize="small" titleAccess="Archived" />
          ) : null,
      },
      { field: "name", headerName: "Name", width: 260 },
      {
        field: "type",
        headerName: "Type",
        width: 140,
        renderCell: (params) => typeLabel(params.row.type),
      },
      { field: "wetWeightLabel", headerName: "Wet Weight", width: 140 },
      { field: "dryWeightLabel", headerName: "Dry Substrate", width: 160 },
      { field: "dimsLabel", headerName: "Dimensions", width: 180 },
      { field: "notes", headerName: "Notes", width: 360 },
    ];

    if (!props.units) return null;
    if (!initialState) return <CircularProgress />;

    return (
      <StripedDataGrid
        apiRef={apiRef}
        columns={columns}
        rows={props.units.map((u) => ({
          id: u.id,
          name: u.name,
          type: u.type,
          wetWeightLabel: fmtWeight(u.wetWeight, u.wetWeightUom),
          dryWeightLabel: fmtWeight(
            u.drySubstrateWeight,
            u.drySubstrateWeightUom,
          ),
          dimsLabel: fmtDims(u.length, u.width, u.depth, u.dimUom),
          notes: u.notes,
          archivedAt: u.archivedAt,
        }))}
        initialState={{
          ...initialState,
          filter: undefined,
          sorting: { sortModel: [{ field: "name", sort: "asc" }] },
        }}
        checkboxSelection
        onRowSelectionModelChange={props.onSelectionChange}
        pagination
        paginationMode="client"
        pageSizeOptions={[15, 25, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={(m) => {
          setPaginationModel(m);
          props.onPaginationModelChange(m);
        }}
        showToolbar
        slots={{ toolbar: GridToolbar }}
        density={density}
        onDensityChange={(d) => {
          setDensity(d);
          localStorage.setItem(productionUnitsGridDensityStorageKey, d);
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            csvOptions: { allColumns: true, fileName: csvFileName },
          },
        }}
        getRowClassName={(params: GridRowClassNameParams) => {
          const row = params.row as any;
          if (row.archivedAt) return "archived-row";
          return params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";
        }}
        disableRowSelectionOnClick
        onRowClick={(params: GridRowParams) => props.onClickRow(params.id)}
      />
    );
  },
);

export default function MushroomProductionUnitsPage() {
  const [units, setUnits] = useState<ProductionUnit[] | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);

  const showArchivedCheckedStorageKey =
    "mushroomProductionUnitsShowArchivedChecked";
  const showActiveCheckedStorageKey =
    "mushroomProductionUnitsShowActiveChecked";

  const [showArchivedChecked, setShowArchivedChecked] = useState(
    window.localStorage.getItem(showArchivedCheckedStorageKey) !==
      false.toString(),
  );
  const [showActiveChecked, setShowActiveChecked] = useState(
    window.localStorage.getItem(showActiveCheckedStorageKey) !==
      false.toString(),
  );

  const [addOpen, setAddOpen] = useState(false);

  const [addName, setAddName] = useState("");
  const [addType, setAddType] = useState<ProductionUnitType>("bag");

  const [addWetWeight, setAddWetWeight] = useState<string>("10");
  const [addWetWeightUom, setAddWetWeightUom] = useState<WeightUom>("lb");

  const [addDryWeight, setAddDryWeight] = useState<string>("");
  const [addDryWeightUom, setAddDryWeightUom] = useState<WeightUom>("lb");

  const [addLength, setAddLength] = useState<string>("10");
  const [addWidth, setAddWidth] = useState<string>("20");
  const [addDepth, setAddDepth] = useState<string>("");
  const [addDimUom, setAddDimUom] = useState<DimUom>("in");

  const [addNotes, setAddNotes] = useState("");

  const history = useHistory();
  const initialPage = getSearchParamNumber(history, "page", 0);

  useEffect(() => {
    const raw = window.localStorage.getItem(productionUnitsStorageKey);
    if (raw) {
      try {
        setUnits(JSON.parse(raw) as ProductionUnit[]);
        return;
      } catch {
        window.localStorage.removeItem(productionUnitsStorageKey);
      }
    }

    const defaults: ProductionUnit[] = [
      {
        id: 1,
        name: "10 lb Bag",
        type: "bag",
        wetWeight: 10,
        wetWeightUom: "lb",
        drySubstrateWeight: null,
        drySubstrateWeightUom: null,
        length: null,
        width: null,
        depth: null,
        dimUom: null,
        notes: null,
        archivedAt: null,
      },
      {
        id: 2,
        name: "5 lb Bag",
        type: "bag",
        wetWeight: 5,
        wetWeightUom: "lb",
        drySubstrateWeight: null,
        drySubstrateWeightUom: null,
        length: null,
        width: null,
        depth: null,
        dimUom: null,
        notes: null,
        archivedAt: null,
      },
      {
        id: 3,
        name: "1020 Tray",
        type: "tray",
        wetWeight: null,
        wetWeightUom: null,
        drySubstrateWeight: null,
        drySubstrateWeightUom: null,
        length: 10,
        width: 20,
        depth: null,
        dimUom: "in",
        notes: null,
        archivedAt: null,
      },
    ];

    setUnits(defaults);
    window.localStorage.setItem(
      productionUnitsStorageKey,
      JSON.stringify(defaults),
    );
  }, []);

  const persistUnits = (next: ProductionUnit[]) => {
    setUnits(next);
    window.localStorage.setItem(
      productionUnitsStorageKey,
      JSON.stringify(next),
    );
  };

  const handleSelectionChange = (m: GridRowSelectionModel) => {
    let ids: number[] = [];
    const anyM = m as any;
    if (Array.isArray(m)) ids = m.map((v) => Number(v));
    else if (anyM?.ids?.forEach) {
      const t: number[] = [];
      anyM.ids.forEach((v: unknown) => t.push(Number(v)));
      ids = t;
    }
    setSelected(ids.length ? [ids[0]] : []);
  };

  const handleShowActiveChanged = (checked: boolean) => {
    setShowActiveChecked(checked);
    window.localStorage.setItem(
      showActiveCheckedStorageKey,
      checked.toString(),
    );
  };

  const handleShowArchivedChanged = (checked: boolean) => {
    setShowArchivedChecked(checked);
    window.localStorage.setItem(
      showArchivedCheckedStorageKey,
      checked.toString(),
    );
  };

  const onClickRow = (_id: GridRowId) => {
    // later: open edit page/modal
    return;
  };

  const handleClickAdd = () => {
    setAddName("");
    setAddType("bag");

    setAddWetWeight("10");
    setAddWetWeightUom("lb");

    setAddDryWeight("");
    setAddDryWeightUom("lb");

    setAddLength("10");
    setAddWidth("20");
    setAddDepth("");
    setAddDimUom("in");

    setAddNotes("");
    setAddOpen(true);
  };

  const handleCloseAdd = () => setAddOpen(false);

  const handleSaveAdd = async () => {
    if (!units) return;
    if (!addName.trim()) return;

    setBusy(true);
    try {
      const maxId = units.reduce((m, u) => Math.max(m, u.id), 0);

      const wantsDims = addType === "tray";
      const wantsWeight = addType !== "tray";

      const next: ProductionUnit = {
        id: maxId + 1,
        name: addName.trim(),
        type: addType,

        wetWeight: wantsWeight ? Number(addWetWeight) || 0 : null,
        wetWeightUom: wantsWeight ? addWetWeightUom : null,

        drySubstrateWeight:
          wantsWeight && addDryWeight.trim() !== ""
            ? Number(addDryWeight) || 0
            : null,
        drySubstrateWeightUom:
          wantsWeight && addDryWeight.trim() !== "" ? addDryWeightUom : null,

        length: wantsDims ? Number(addLength) || 0 : null,
        width: wantsDims ? Number(addWidth) || 0 : null,
        depth:
          wantsDims && addDepth.trim() !== "" ? Number(addDepth) || 0 : null,
        dimUom: wantsDims ? addDimUom : null,

        notes: addNotes.trim() ? addNotes.trim() : null,
        archivedAt: null,
      };

      persistUnits([next, ...units]);
      setAddOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const filteredUnits =
    units?.filter(
      (u) =>
        (showActiveChecked && u.archivedAt === null) ||
        (showArchivedChecked && u.archivedAt !== null),
    ) ?? [];

  return (
    <Page title="Production Units">
      {units && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleClickAdd}
              disabled={busy}
            >
              Add
            </Button>

            <IconButton
              aria-label="delete"
              disabled={busy || selected?.length === 0}
              onClick={() => {
                // later: archive/unarchive/delete
                return;
              }}
              size="large"
            >
              <DeleteIcon />
            </IconButton>

            <ActiveFilterControls
              showActiveChecked={showActiveChecked}
              showArchivedChecked={showArchivedChecked}
              onChangeShowActive={handleShowActiveChanged}
              onChangeShowArchived={handleShowArchivedChanged}
            />
          </Box>

          <Box sx={{ height: 750, width: "100%", pt: 2 }}>
            <ProductionUnitsDataGrid
              units={filteredUnits}
              onSelectionChange={handleSelectionChange}
              initialPage={initialPage}
              onPaginationModelChange={(m) => {
                const params = new URLSearchParams(`page=${m.page}`);
                history.replace({ search: params.toString() });
              }}
              onClickRow={onClickRow}
            />
          </Box>

          <Dialog
            open={addOpen}
            onClose={handleCloseAdd}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Add Production Unit</DialogTitle>
            <DialogContent>
              <Box
                sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}
              >
                <TextField
                  label="Name"
                  value={addName}
                  onChange={(e) => setAddName(e.target.value)}
                  fullWidth
                />

                <TextField
                  label="Type"
                  value={addType}
                  onChange={(e) =>
                    setAddType(e.target.value as ProductionUnitType)
                  }
                  select
                  fullWidth
                >
                  <MenuItem value="bag">Bag</MenuItem>
                  <MenuItem value="tray">Tray</MenuItem>
                  <MenuItem value="block">Block</MenuItem>
                  <MenuItem value="log">Log</MenuItem>
                  <MenuItem value="bottle">Bottle</MenuItem>
                  <MenuItem value="column">Column</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </TextField>

                {addType === "tray" ? (
                  <>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <TextField
                        label="Length"
                        value={addLength}
                        onChange={(e) => setAddLength(e.target.value)}
                        type="number"
                        fullWidth
                      />
                      <TextField
                        label="Width"
                        value={addWidth}
                        onChange={(e) => setAddWidth(e.target.value)}
                        type="number"
                        fullWidth
                      />
                    </Box>

                    <Box sx={{ display: "flex", gap: 2 }}>
                      <TextField
                        label="Depth (optional)"
                        value={addDepth}
                        onChange={(e) => setAddDepth(e.target.value)}
                        type="number"
                        fullWidth
                      />
                      <TextField
                        label="Units"
                        value={addDimUom}
                        onChange={(e) => setAddDimUom(e.target.value as DimUom)}
                        select
                        fullWidth
                      >
                        <MenuItem value="in">in</MenuItem>
                        <MenuItem value="cm">cm</MenuItem>
                      </TextField>
                    </Box>
                  </>
                ) : (
                  <>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <TextField
                        label="Wet Weight"
                        value={addWetWeight}
                        onChange={(e) => setAddWetWeight(e.target.value)}
                        type="number"
                        fullWidth
                      />
                      <TextField
                        label="Units"
                        value={addWetWeightUom}
                        onChange={(e) =>
                          setAddWetWeightUom(e.target.value as WeightUom)
                        }
                        select
                        fullWidth
                      >
                        <MenuItem value="lb">lb</MenuItem>
                        <MenuItem value="kg">kg</MenuItem>
                      </TextField>
                    </Box>

                    <Box sx={{ display: "flex", gap: 2 }}>
                      <TextField
                        label="Dry Substrate Weight (optional)"
                        value={addDryWeight}
                        onChange={(e) => setAddDryWeight(e.target.value)}
                        type="number"
                        fullWidth
                      />
                      <TextField
                        label="Units"
                        value={addDryWeightUom}
                        onChange={(e) =>
                          setAddDryWeightUom(e.target.value as WeightUom)
                        }
                        select
                        fullWidth
                      >
                        <MenuItem value="lb">lb</MenuItem>
                        <MenuItem value="kg">kg</MenuItem>
                      </TextField>
                    </Box>
                  </>
                )}

                <TextField
                  label="Notes"
                  value={addNotes}
                  onChange={(e) => setAddNotes(e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                />
              </Box>
            </DialogContent>

            <DialogActions>
              <Button onClick={handleCloseAdd} disabled={busy}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveAdd}
                color="primary"
                variant="contained"
                disabled={busy || !addName.trim()}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      <LoadingBackdrop open={busy} />
    </Page>
  );
}
